import { useEffect, useState } from "react";
import { Box, HBox } from "../box/Box";
import Button from "../button/Button";
import { Checkbox } from "../checkbox/Checkbox";
import { Error } from "../error/Error";
import { IconClose } from "../icons/IconClose";
import { IconCopy } from "../icons/IconCopy";
import Input from "../input/Input";
import { HLine } from "../lines/HLine";
import { Select } from "../select/Select";
import Spacer from "../spacer/Spacer";
import { Typo } from "../text/Typo";
import { RecursiveCSS } from "../theme/GlobalTheme";

interface IFormParser {
	onSubmit: (data: any) => Promise<any>;
	onComplete?: (res?: string) => void;
	onRequest: () => Promise<any>;
	actionButton?: {
		label: string;
		action: (data: [any, React.Dispatch<any>]) => void;
	};
}

export const FormParser = ({ onSubmit, onRequest, onComplete, actionButton }: IFormParser) => {
	const error = useState<any | null>(null);
	const data = useState<any | null>(null);
	const busy = useState<boolean>(false);
	const unsavedCganges = useState<boolean>(false);

	useEffect(() => {
		sendRequest();
	}, []);

	const sendRequest = async () => {
		if (unsavedCganges[0]) {
			/*S_CONFIRM.invoke({
				message: "Confirm?",
				onResult: (val: "confirm" | "reject") => {
					if (val === "confirm") {
						reloadData();
					}
				},
			});*/
		} else {
			reloadData();
		}
	};

	const reloadData = async () => {
		unsavedCganges[1](false);
		busy[1](true);
		error[1](null);
		const result = await onRequest();
		setData(result);

		busy[1](false);
	};

	const setData = (result: any) => {
		if (result && typeof result === "object") {
			let err = false;
			if ("error" in result && typeof result.error === "object" && "code" in result.error) {
				err = true;
				error[1](result.error);
			}

			if (!err && "code" in result && "message" in result) {
				err = true;
				error[1](result);
			}

			if (!err && result) {
				unsavedCganges[1](false);
				if (result.data) {
					data[1](getProxy(result.data));
				} else data[1](getProxy(result));
			}
		} else {
			error[1]({ code: -1, message: "wrong data" });
		}
	};

	const getProxy = (val: any) => {
		const proxyConfig = {
			set: (obj: any, property: string, value: any) => {
				unsavedCganges[1](true);
				return Reflect.set(obj, property, value);
			},
			get: (target: any, key: any) => {
				if (typeof target[key] === "object" && target[key] !== null) {
					return new Proxy(target[key], proxyConfig);
				} else {
					return target[key];
				}
			},
		};

		const proxy = new Proxy(val, proxyConfig);
		return proxy;
	};

	const sendSubmit = async () => {
		busy[1](true);
		error[1](null);
		const result = await onSubmit(data[0]);

		setData(result);
		onComplete && onComplete(result);
	};

	let content = null;
	if (data[0]) {
		content = (
			<>
				<Block data={data[0].data ?? data[0]} parent={null} />
				<Spacer size="20px" />
				<HBox>
					<Spacer grow />

					{actionButton && (
						<>
							<Button variant="secondarySmall" onClick={() => actionButton.action(data)} title={actionButton.label} />
							<Spacer size="10px" />
						</>
					)}

					<Button variant="secondarySmall" onClick={() => sendRequest()} title="Reset" />
					<Spacer size="10px" />
					<Button variant="primarySmall" onClick={() => sendSubmit()} title="Send" />
				</HBox>
			</>
		);
	}

	return (
		<Box
			sx={{
				maxWidth: "800px",
			}}>
			{unsavedCganges[0] && (
				<Box sx={{ paddingBottom: "10px", width: "100%" }}>
					<Typo
						sx={{
							boxSizing: "border-box",
							borderRadius: "6px",
							padding: "10px",
							fontSize: "11px",
							backgroundColor: "rgb(111 111 145)",
							boxShadow: "0px 2px 2px rgba(0,0,0,.1)",
							color: "white",
						}}>
						Changes not saved
					</Typo>
				</Box>
			)}
			{error[0] && <Error error={error[0]} />}
			{content}
		</Box>
	);
};

interface IBlockProps {
	data: any;
	parent: any;
	parentIndex?: string;
	parentOptions?: Record<string, string> | null;
}
interface IBlockItem {
	name: string;
	content: any;
	options: Record<string, string> | null;
	container: any;
}
export const Block = (props: IBlockProps) => {
	const [state, setState] = useState<number>(0);

	let items: IBlockItem[] = [];
	if (props.data && typeof props.data === "object") {
		for (let i in props.data) {
			if (i.startsWith("$__")) continue;

			let options: Record<string, string> | null = null;
			// find options
			for (let j in props.data) {
				if (j.startsWith("$__" + i + "_")) {
					const oname = j.replace("$__" + i + "_", "");
					if (!options) options = {};
					if (oname) options[oname] = props.data[j];
				}
			}

			items.push({
				name: i,
				content: props.data[i],
				options: options,
				container: props.data,
			});
		}
	}

	const returnContent = items.map((val: IBlockItem, index: number) => {
		const key = (props.parentIndex ?? "^") + "_" + index;

		const sx: RecursiveCSS = {};

		if (val.options && val.options["hidden"]) {
			return null;
		}

		// Block with inner data
		if (typeof val.content === "object") {
			return (
				<Box
					sx={{
						//display: "contents",
						padding: "20px",
						backgroundColor: "#FFFFFF",
						borderRadius: "6px",
						boxShadow: "0px 1px 2px rgba(0,0,0,.2)",
						display: "grid",
						gap: "15px",

						...sx,
					}}
					key={key}>
					{
						/*SETUP BLOCK NAME*/
						!Array.isArray(props.data) && (
							<>
								<Typo variant="medium" bold>
									{createName(val.name)}
								</Typo>
							</>
						)
					}

					{
						/*Render block*/
						!Array.isArray(val.content) && <Block data={val.content} parent={props.data} parentOptions={val.options} parentIndex={index + "_"} />
					}

					{
						/*Render Array*/
						Array.isArray(val.content) && (
							<Box
								sx={{
									display: "grid",
									gap: "10px",
								}}>
								{val.content.map((arritem: any, arrindex: number) => {
									//hide content
									let fields = 0;
									let emptyFields = 0;
									if (val.options?.hideEmpty && typeof arritem === "object") {
										for (let i in arritem) {
											if (i.startsWith("$__")) continue;
											fields++;
											if (arritem[i] === "" || arritem[i] === null) emptyFields++;
										}
										if (emptyFields === fields) return null;
									}

									const innerKey = key + "_" + arrindex;

									let gridSX: RecursiveCSS = {};
									if (val.options && val.options.gridColumns) {
										gridSX = {
											gridTemplateColumns: val.options.gridColumns,
											display: "grid",
											gap: "20px",
											alignItems: "center",
										};
									}

									const rmbutton = val.content && val.content.length > 1;

									return (
										<>
											<Box
												key={innerKey + "_A"}
												sx={{
													display: "grid",
													alignItems: "center",
													gap: "10px",
													gridTemplateColumns: "1fr " + (rmbutton ? "max-content" : ""),
												}}>
												<Box sx={{ ...gridSX }}>
													<Block data={arritem} parent={val.content} parentIndex={innerKey} />
												</Box>

												{rmbutton && (
													<Button
														variant="secondarySmall"
														sx={{
															padding: "4px",
														}}
														onClick={() => {
															if (val.content && Array.isArray(val.content)) {
																val.content.splice(arrindex, 1);
																setState(state + 1);
															}
														}}>
														<IconClose />
													</Button>
												)}
											</Box>
											{arrindex + 1 < val.content.length && val.options?.aslist && <HLine key={innerKey + "_B"} color="rgba(0,0,0,.1)" />}
										</>
									);
								})}

								<HBox>
									<Spacer grow />
									<Button
										variant="secondarySmall"
										onClick={() => {
											console.log(val.content);

											// ADD value from possible values
											if (val.options?.possible_values && Array.isArray(val.options.possible_values)) {
												/*S_SELECT_ITEMS_POPUP.invoke({
													title: "Select " + createName(val.name),
													items: val.options.possible_values,
													multi: true,
													searchable: false,

													onSelected: items => {
														for (let item of items) {
															if (val.content && Array.isArray(val.content)) {
																let original = val.content[0];
																if (!original) {
																	console.error("No original object!");
																	return;
																}
																let newitem = JSON.parse(JSON.stringify(original));
																for (let i in original) {
																	if (i in item) newitem[i] = item[i];
																}
																val.content.push(newitem);
															}
														}
														setState(state + 1);
													},
												});*/
												return;
											}

											if (val.options?.type === "country") {
												// add as country
												/*S_SELECT_COUNTRIES_POPUP.invoke({
													onSuccess: countries => {
														for (let index = 0; index < countries.length; index++) {
															const country = countries[index];
															const newblock = JSON.parse(JSON.stringify(val.content[0]));
															for (const key in country) {
																if (key in newblock) newblock[key] = (country as any)[key];
															}
															let found = false;
															for (let j of val.content) {
																if (JSON.stringify(j).toLowerCase() === JSON.stringify(newblock)) {
																	found = true;
																	break;
																}
															}
															if (!found) val.content.push(newblock);
														}

														setState(state + 1);
													},
												});*/
											} else {
												// add item
												if (val.content && val.content.length) {
													val.content.push(JSON.parse(JSON.stringify(val.content[val.content.length - 1])));
													setState(state + 1);
												}
											}
										}}>
										+ Add {createName(val.name)}
									</Button>
								</HBox>
							</Box>
						)
					}
				</Box>
			);
		}

		return <InputBlock key={key} data={val} />;
	});

	if (Array.isArray(props.parent)) return returnContent;

	let gridBox: RecursiveCSS | null = null;
	if (props.parentOptions && props.parentOptions.gridColumns) {
		gridBox = {
			display: "grid",
			gridTemplateColumns: props.parentOptions.gridColumns, //"repeat(auto-fit, minmax(clamp(100px, 350px, 200px), 1fr))"
		};
	}

	return (
		<Box
			sx={{
				display: "grid",
				gap: "30px",
				...gridBox,
			}}>
			{returnContent}
		</Box>
	);
};

const InputBlock = ({ data }: { data: IBlockItem }) => {
	const [state, setState] = useState<number>(0);

	const name = data.name;
	let type = data.options?.type ?? "text";
	if (typeof data.content === "boolean") type = "boolean";

	if (data.options?.hidden) return null;
	let disabled = false;
	if (data.options?.disabled) disabled = true;

	let content = null;
	switch (type) {
		case "select":
			let values = data.options?.values ?? [];

			content = <Select name={name} label={createName(name)} data={data.container} values={values as any[]} />;
			break;

		case "":
			break;

		case "plain":
			if (!data.container[name]) return null;
			content = <Typo variant="medium">{data.container[name]}</Typo>;
			break;

		case "flag":
			if (!data.container[name]) return null;
			content = (
				<>
					<Box
						sx={{
							width: "35px",
							height: "25px",
							minWidth: "35px",
							minHeight: "25px",
							backgroundSize: "cover",
							boxShadow: "0px 1px 2px rgba(0,0,0,.2)",
							border: "2px solid #FFFFFF",
							borderRadius: "2px",
						}}
						style={{
							backgroundImage: "url(https://flagcdn.com/" + data.container[name]?.toLowerCase() + ".svg",
						}}></Box>
				</>
			);
			break;

		case "boolean":
			content = <Checkbox name={name} sx={{}} label={createName(name)} data={data.container} />;
			break;

		case "sha256":
			content = (
				<HBox gap="10px">
					<Input
						disabled={disabled}
						key={state}
						name={name}
						label={createName(name)}
						data={data.container}
						sx={{ flexGrow: "1" }}
						iconRight={
							<Button
								variant="icon"
								sx={{ padding: "14px", margin: "2px", aspectRatio: "1 / 1" }}
								icon={<IconCopy size={"12"} />}
								onClick={async () => {
									if (data.container[name]) {
										if ("clipboard" in navigator) {
											await navigator.clipboard.writeText(data.container[name]);
										} else {
											document.execCommand("copy", true, data.container[name]);
										}
										/*toast.info("Copied", {
											position: "top-center",
											autoClose: 2000,
											hideProgressBar: true,
											closeOnClick: true,
											pauseOnHover: true,
											draggable: false,
											progress: undefined,
											theme: "light",
											transition: Slide,
										});*/
									}
								}}
							/>
						}
					/>
					<Button
						sx={{ alignSelf: "end", height: "46px" }}
						variant="secondarySmall"
						onClick={() => {
							const process = () => {
								data.container[name] = "";
								setState(state + 1);
							};
							if (data.container[name]) {
								/*S_CONFIRM.invoke({
									message: "Confirm token clear?",
									onResult: (val: "confirm" | "reject") => {
										if (val === "confirm") {
											process();
										}
									},
								});*/
							} else {
								process();
							}
						}}
						title="Clear"
					/>
					<Button
						sx={{ alignSelf: "end", height: "46px" }}
						variant="secondarySmall"
						onClick={async () => {
							const process = async () => {
								const newValue = ""; //await randomSHA256();
								data.container[name] = newValue;
								setState(state + 1);
							};
							if (data.container[name]) {
								/*S_CONFIRM.invoke({
									message: "Confirm token change?",
									onResult: (val: "confirm" | "reject") => {
										if (val === "confirm") {
											process();
										}
									},
								});*/
							} else {
								process();
							}
						}}
						title="Generate"
					/>
				</HBox>
			);
			break;

		//!TODO: CASE currency, phone, country

		default:
			content = <Input disabled={disabled} name={name} label={createName(name)} data={data.container} />;
	}

	return content;
};

// utils
export const createName = (str: string): string => {
	if (!str) return "";
	str = str.replaceAll("_", " ").trim();
	if (str.toLowerCase() === "email") str = "e-mail";
	return str.charAt(0).toUpperCase() + str.slice(1);
};
